import { KeyValuePipe } from '@angular/common';
import { Component, Input, OnInit, OnDestroy, inject, Output, EventEmitter } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteTrigger, MatAutocomplete } from '@angular/material/autocomplete';
import { MatOptgroup, MatOption } from '@angular/material/core';
import { MatFormField } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { DocumentSubType } from '@model/ftf-api.model';
import { LoggerService } from '@shared/service/logger.service';
import { Subject } from 'rxjs';
import { takeUntil, startWith, map } from 'rxjs/operators';

@Component({
  selector: 'app-doc-sub-type-select',
  templateUrl: './doc-sub-type-select.component.html',
  styleUrls: ['./doc-sub-type-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: DocSubTypeSelectComponent,
      multi: true,
    },
  ],
  imports: [
    MatFormField,
    MatInput,
    FormsModule,
    MatAutocompleteTrigger,
    ReactiveFormsModule,
    MatAutocomplete,
    MatOptgroup,
    MatOption,
    KeyValuePipe,
  ],
})
export class DocSubTypeSelectComponent implements OnInit, OnDestroy, ControlValueAccessor {
  private loggerService = inject(LoggerService);

  @Input() subtypes: Record<string, DocumentSubType[]> = {};
  @Input() grouped = true;
  @Input() placeholder = 'Search sub-types';
  @Input() panelWidth = '600px';
  @Output() selectionChange = new EventEmitter<DocumentSubType | null>();

  // Efficient data structures
  flatSubtypes: DocumentSubType[] = [];
  subtypeMap = new Map<number, DocumentSubType>();
  groupedData: { [key: string]: DocumentSubType[] } = {};

  searchControl = new FormControl('');
  filteredGroups: { [key: string]: DocumentSubType[] } = {};
  filteredFlat: DocumentSubType[] = [];
  value: number | null = null;

  private destroy$ = new Subject<void>();
  private onChange: (value: number | null) => void = () => {};
  private onTouched: () => void = () => {};

  /**
   * Angular lifecycle hook that is called after the component's data-bound properties have been initialized.
   *
   * - Initializes the component's data by calling `prepareData()`.
   * - Sets up a subscription to the `searchControl`'s value changes to filter data dynamically:
   *   - Handles different value types (null, string, number) to ensure correct filtering behavior.
   *   - Uses RxJS operators to manage the observable stream and clean up on component destruction.
   *   - Updates the filtered data in response to user input or selection.
   */
  ngOnInit(): void {
    this.prepareData();

    // Set up filtering
    this.searchControl.valueChanges
      .pipe(
        startWith(''),
        takeUntil(this.destroy$),
        map((value) => {
          // Handle various value types properly
          if (value === null || value === undefined) {
            return this.filterData('');
          }

          // Handle string values (user typing in search box)
          if (typeof value === 'string') {
            return this.filterData(value);
          }

          // Handle numeric values (item ID selection)
          return this.filterData(''); // Reset filter when selection happens
        })
      )
      .subscribe(() => {
        // Filtered data updated in filterData method
      });
  }

  // Process data into efficient structures
  // Process data into efficient structures with proper type handling
  private prepareData(): void {
    this.subtypeMap.clear();
    this.flatSubtypes = [];
    // Handle empty or invalid input
    if (!this.subtypes) {
      this.loggerService.warn('[DocSubTypeSelect] No subtypes provided');
      this.filteredFlat = [];
      this.filteredGroups = {};
      return;
    }

    if (this.grouped && typeof this.subtypes === 'object' && !Array.isArray(this.subtypes)) {
      try {
        this.groupedData = { ...this.subtypes };

        // Process each group and build flat list + map
        Object.entries(this.subtypes).forEach(([group, items]) => {
          if (!Array.isArray(items)) return;

          this.flatSubtypes.push(...items);

          items.forEach((item) => {
            if (item && typeof item === 'object' && item.id !== undefined) {
              const itemWithGroup = item as DocumentSubType & { assignedGroup?: string };
              itemWithGroup.assignedGroup = group;
              this.subtypeMap.set(item.id, item);
            }
          });
        });
        // Initialize filtered groups with original data
        this.filteredGroups = { ...this.subtypes };

        this.loggerService.debug(
          `[DocSubTypeSelect] Prepared ${this.flatSubtypes.length} items in ${Object.keys(this.subtypes).length} groups`
        );
      } catch (error) {
        this.loggerService.error('[DocSubTypeSelect] Error processing grouped data:', error);
      }
    } else if (!this.grouped && typeof this.subtypes === 'object' && !Array.isArray(this.subtypes)) {
      try {
        const keys = Object.keys(this.subtypes);

        if (keys.length === 1 && Array.isArray(this.subtypes[keys[0]])) {
          // Extract items from the single key
          const items = this.subtypes[keys[0]];
          this.flatSubtypes = [...items];

          // Build the lookup map
          this.flatSubtypes.forEach((item) => {
            if (item && typeof item === 'object' && item.id !== undefined) {
              this.subtypeMap.set(item.id, item);
            }
          });
          // Initialize filtered flat list
          this.filteredFlat = [...this.flatSubtypes];
          this.loggerService.debug(`[DocSubTypeSelect] Prepared ${this.flatSubtypes.length} items from single group '${keys[0]}'`);
        } else {
          this.loggerService.warn(
            '[DocSubTypeSelect] Expected object with single key containing array for flat mode, received:',
            this.subtypes
          );
          this.filteredFlat = [];
        }
      } catch (error) {
        this.loggerService.error('[DocSubTypeSelect] Error processing data in flat mode:', error);
        this.filteredFlat = [];
      }
    } else {
      this.loggerService.warn('[DocSubTypeSelect] Unexpected subtypes format:', typeof this.subtypes);
      this.filteredFlat = [];
    }
  }

  // Filter the data based on input
  /**
   * Filters the document subtypes based on the provided search text.
   *
   * - If `this.grouped` is `true`, filters grouped data (`this.groupedData`) and updates `this.filteredGroups`.
   * - If `this.grouped` is `false`, filters flat data (`this.flatSubtypes`) and updates `this.filteredFlat`.
   * - Filtering is case-insensitive and matches items whose `name` property includes the search text.
   *
   * @param searchText - The text to filter document subtypes by. Non-string values are converted to strings.
   */
  private filterData(searchText: string): void {
    if (typeof searchText !== 'string') {
      searchText = searchText ? String(searchText) : '';
      this.loggerService.warn('[DocSubTypeSelect] filterData received non-string value:', searchText);
    }

    const filterValue = searchText.toLowerCase();

    if (this.grouped) {
      // Filter grouped data
      if (!filterValue) {
        this.filteredGroups = { ...this.groupedData };
        return;
      }

      const result: { [key: string]: any[] } = {};

      Object.entries(this.groupedData).forEach(([group, items]) => {
        if (Array.isArray(items)) {
          const filtered = items.filter((item) => item.name?.toLowerCase().includes(filterValue));
          if (filtered.length > 0) {
            result[group] = filtered;
          }
        }
      });

      this.filteredGroups = result;
    } else {
      // Filter flat data
      if (!filterValue) {
        this.filteredFlat = [...this.flatSubtypes];
        return;
      }

      this.filteredFlat = this.flatSubtypes.filter((item) => item.name?.toLowerCase().includes(filterValue));
    }
  }

  // Display function using map for lookup - O(1) efficiency
  displayFn = (id: number | null | undefined): string => {
    if (id == null) return '';
    const subtype = this.subtypeMap.get(id);
    return subtype?.name || '';
  };

  /**
   * Writes a new value to the component and updates the search control's displayed value.
   *
   * @param value - The value to be written to the component.
   *
   * Sets the internal `value` property and updates the `searchControl` form control
   * with a display-friendly version of the value, without emitting change events.
   */
  writeValue(value: any): void {
    this.value = value;
    this.searchControl.setValue(this.displayFn(value), { emitEvent: false });
  }

  /**
   * Registers a callback function that should be called when the value of the component changes.
   * This method is part of the ControlValueAccessor interface, allowing the component to work with Angular forms.
   *
   * @param fn - The callback function to be invoked when the component's value changes.
   */
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  /**
   * Registers a callback function that should be called when the control is touched.
   * This method is part of the ControlValueAccessor interface, allowing the component
   * to integrate with Angular forms and notify the forms API when the component has been interacted with.
   *
   * @param fn - The callback function to be invoked when the control is touched.
   */
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  /**
   * Sets the disabled state of the search control.
   *
   * This method is called by Angular forms to enable or disable the control
   * based on the provided boolean value.
   *
   * @param isDisabled - If `true`, disables the search control; if `false`, enables it.
   */
  setDisabledState(isDisabled: boolean): void {
    isDisabled ? this.searchControl.disable() : this.searchControl.enable();
  }

  /**
   * Handles the selection change event from the autocomplete or dropdown component.
   * Updates the internal value, notifies the form control of the change and touch event,
   * and sets the display text in the search control after a short delay.
   *
   * @param event - The selection change event containing the selected option and its value.
   */
  onSelectionChange(event: any): void {
    this.value = event.option.value;
    this.onChange(this.value); // Notify form of change
    this.onTouched();
    this.selectionChange.emit(this.value ? this.subtypeMap.get(this.value) : null); // Emit the selected DocumentSubType
    setTimeout(() => {
      const displayText = this.displayFn(this.value);
      this.searchControl.setValue(displayText, { emitEvent: false });
    });
  }

  /**
   * Handles the dropdown close event.
   *
   * If a value is selected, ensures that the search control's value matches the display text
   * of the selected value. If not, updates the search control's value without emitting change events.
   */
  onDropdownClosed(): void {
    if (this.value !== null && this.value !== undefined) {
      const displayText = this.displayFn(this.value);
      if (this.searchControl.value !== displayText) {
        this.searchControl.setValue(displayText, { emitEvent: false });
      }
    }
  }

  /**
   * Lifecycle hook that is called when the component is destroyed.
   * Emits a value and completes the `destroy$` subject to clean up subscriptions
   */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
